/**
 * Checks if pathname matches a route (exact or any sub-route under it)
 * @param {string} itemPath - Menu item path e.g. '/product-master/pre-ipo/inventory'
 * @param {string} pathname - Current pathname e.g. '/product-master/pre-ipo/inventory/14/update'
 * @returns {boolean}
 */
export const isPathActive = (itemPath, pathname) => {
  if (!itemPath) return false;
  if (itemPath.includes(':')) {
    const pattern = itemPath
      .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      .replace(/:[^/]+/g, '[^/]+');
    const matcher = new RegExp(`^${pattern}(?:/|$)`);
    return matcher.test(pathname);
  }
  return pathname === itemPath || pathname.startsWith(itemPath + '/');
};

const getItemPaths = (item) => {
  const paths = [];
  if (item?.path) paths.push(item.path);
  if (Array.isArray(item?.activePaths)) {
    paths.push(...item.activePaths.filter(Boolean));
  }
  return paths;
};

const isAnyItemPathActive = (item, pathname) => {
  return getItemPaths(item).some((path) => isPathActive(path, pathname));
};

/**
 * Checks if any descendant of item (at any nesting level) matches pathname
 * @param {Object} item - Sidebar item with optional path and children
 * @param {string} pathname - Current pathname
 * @returns {boolean}
 */
const hasActiveDescendant = (item, pathname) => {
  if (!item.children || item.children.length === 0) return false;
  return item.children.some((child) => {
    if (isAnyItemPathActive(child, pathname)) return true;
    return hasActiveDescendant(child, pathname);
  });
};

/**
 * Checks if a sidebar item is active based on the current pathname.
 * Item is active if its path matches pathname (or a sub-route), or any descendant's path does.
 * @param {Object} item - The sidebar item to check
 * @param {string} pathname - The current pathname from useLocation().pathname
 * @returns {boolean} - Whether the item is active
 */
export const isItemActive = (item, pathname) => {
  if (isAnyItemPathActive(item, pathname)) {
    return true;
  }
  if (item.children) {
    return item.children.some(
      (child) =>
        isAnyItemPathActive(child, pathname) ||
        hasActiveDescendant(child, pathname)
    );
  }
  return false;
};

/**
 * Returns the index of the top-level item that contains the current path (has a matching descendant).
 * Used to auto-expand the parent when visiting a deep route.
 * @param {Array} items - Top-level sidebar items
 * @param {string} pathname - Current pathname
 * @returns {number|null} - Index to open, or null
 */
export const getOpenSubmenuIndex = (items, pathname) => {
  if (!items?.length) return null;
  const index = items.findIndex((item) => isItemActive(item, pathname) && item.children?.length > 0);
  return index >= 0 ? index : null;
}; 